import { useState, useEffect, useRef, useCallback } from 'react'
import { writingAssistantService } from '../../services/writingAssistantService'
import { websocketService } from '../../services/websocketService'
import type { WritingAssistantListItem } from '../../types/writing'
import { markdownToHtml, htmlToMarkdown } from '../../utils/contentConverters'

export interface CollaboratorInfo {
  userId: string
  userName: string
}

interface UseWritingAssistantContentProps {
  writingAssistants: WritingAssistantListItem[]
  selectedLanguages: Record<string, string>
  isClosedCrisis: boolean
  /** ID of the writing assistant currently open for editing, or null. */
  activeWritingId?: string | null
}

export function useWritingAssistantContent({
  writingAssistants,
  selectedLanguages,
  isClosedCrisis,
  activeWritingId = null,
}: UseWritingAssistantContentProps) {
  const [contentStates, setContentStates] = useState<Record<string, string>>({})
  const [originalContentStates, setOriginalContentStates] = useState<Record<string, string>>({})
  const [lastSavedAt, setLastSavedAt] = useState<Record<string, Date | null>>({})
  const [isSaving, setIsSaving] = useState<Record<string, boolean>>({})
  const [documentCollaborators, setDocumentCollaborators] = useState<Record<string, CollaboratorInfo[]>>({})
  const debouncedSaveFunctions = useRef<Record<string, ReturnType<typeof setTimeout>>>({})
  const prevContentStates = useRef<Record<string, string>>({})
  const prevOriginalContentStates = useRef<Record<string, string>>({})

  // Get current content for a writing assistant based on selected language
  const getCurrentContent = useCallback(
    (writingAssistant: WritingAssistantListItem) => {
      const selectedLanguage = selectedLanguages[writingAssistant.id] || 'nl'

      if (selectedLanguage === 'nl') {
        // For Dutch, always use the original generated content
        return writingAssistant.generatedContent
          ? markdownToHtml(writingAssistant.generatedContent)
          : ''
      } else {
        // For other languages, use the translated content if available
        const translations = writingAssistant.translations || {}
        const translatedContent = translations[selectedLanguage]

        if (translatedContent) {
          // Handle both old format (string) and new format (object with content and version)
          if (typeof translatedContent === 'string') {
            return markdownToHtml(translatedContent)
          } else if (translatedContent.content) {
            return markdownToHtml(translatedContent.content)
          }
        }

        return ''
      }
    },
    [selectedLanguages]
  )

  // Initialize content states when writing assistants or selected languages change
  useEffect(() => {
    const newContentStates: Record<string, string> = {}
    const newOriginalContentStates: Record<string, string> = {}

    writingAssistants.forEach(wa => {
      const selectedLanguage = selectedLanguages[wa.id] || 'nl'
      const currentContent = getCurrentContent(wa)
      const contentKey = `${wa.id}-${selectedLanguage}`

      // Update content state: if it doesn't exist or matches the old original (no user changes), update with new content
      const existingContent = prevContentStates.current[contentKey]
      const oldOriginal = prevOriginalContentStates.current[contentKey]

      if (!existingContent || (oldOriginal && existingContent === oldOriginal)) {
        newContentStates[contentKey] = currentContent
      } else {
        // User has made changes, keep them
        newContentStates[contentKey] = existingContent
      }

      // Always update original content state for this language
      newOriginalContentStates[contentKey] = currentContent
    })

    setContentStates(newContentStates)
    setOriginalContentStates(newOriginalContentStates)

    // Update refs for next comparison
    prevContentStates.current = newContentStates
    prevOriginalContentStates.current = newOriginalContentStates
  }, [writingAssistants, selectedLanguages, getCurrentContent])

  // Save content to API
  const saveContent = useCallback(
    async (writingAssistantId: string, htmlContent: string, selectedLanguage: string) => {
      if (isClosedCrisis) return

      // Convert HTML back to markdown for API storage
      const markdownContent = htmlToMarkdown(htmlContent)
      const contentKey = `${writingAssistantId}-${selectedLanguage}`

      // Skip saving if content is unchanged from original
      const originalContent = originalContentStates[contentKey]
      if (originalContent === undefined || htmlToMarkdown(originalContent) === markdownContent)
        return

      setIsSaving(prev => ({ ...prev, [writingAssistantId]: true }))
      try {
        await writingAssistantService.updateWritingAssistantContent(
          writingAssistantId,
          markdownContent
        )
        setLastSavedAt(prev => ({ ...prev, [writingAssistantId]: new Date() }))
        // Update original content state after successful save
        setOriginalContentStates(prev => ({ ...prev, [contentKey]: htmlContent }))
      } catch (error) {
        console.error('Error saving content:', error)
      } finally {
        setIsSaving(prev => ({ ...prev, [writingAssistantId]: false }))
      }
    },
    [isClosedCrisis, originalContentStates]
  )

  // Handle content change with debouncing
  const handleContentChange = useCallback(
    (writingAssistantId: string, content: string) => {
      if (isClosedCrisis) return

      const selectedLanguage = selectedLanguages[writingAssistantId] || 'nl'
      const contentKey = `${writingAssistantId}-${selectedLanguage}`

      setContentStates(prev => ({
        ...prev,
        [contentKey]: content,
      }))

      // Clear existing timeout for this writing assistant
      if (debouncedSaveFunctions.current[writingAssistantId]) {
        clearTimeout(debouncedSaveFunctions.current[writingAssistantId])
      }

      // Set new timeout for auto-save
      debouncedSaveFunctions.current[writingAssistantId] = setTimeout(() => {
        saveContent(writingAssistantId, content, selectedLanguage)
      }, 1500)

      // Broadcast the markdown equivalent to collaborators in real time
      // Only broadcast for the Dutch (source) language to avoid translation confusion
      if (selectedLanguage === 'nl') {
        websocketService.broadcastWritingContentUpdate(
          writingAssistantId,
          htmlToMarkdown(content)
        )
      }
    },
    [isClosedCrisis, saveContent, selectedLanguages]
  )

  // Join / leave collaborative document room when the active document changes
  useEffect(() => {
    if (!activeWritingId || isClosedCrisis) return

    websocketService
      .connect()
      .then(() => websocketService.joinWritingDocument(activeWritingId))
      .catch(err =>
        console.error('[Writing WS] Could not join document room:', err)
      )

    return () => {
      websocketService.leaveWritingDocument()
    }
  }, [activeWritingId, isClosedCrisis])

  // Apply content updates broadcast by collaborators or AI background jobs
  useEffect(() => {
    return websocketService.onWritingContentUpdated(event => {
      // Only apply if we're editing the Dutch version (source language)
      const lang = selectedLanguages[event.writing_id] || 'nl'
      if (lang !== 'nl') return

      const contentKey = `${event.writing_id}-nl`
      const incomingHtml = markdownToHtml(event.content)
      const isAiUpdate = event.source === 'ai'

      setContentStates(prev => {
        const current = prev[contentKey]
        const originalHtml = prevOriginalContentStates.current[contentKey]

        // AI-generated content always wins — it is the new canonical version.
        // For peer edits only apply when the editor has no unsaved local changes.
        if (!isAiUpdate && current !== undefined && current !== originalHtml) return prev

        return { ...prev, [contentKey]: incomingHtml }
      })

      // Update the "original" baseline so subsequent local-save comparisons work
      setOriginalContentStates(prev => ({
        ...prev,
        [contentKey]: incomingHtml,
      }))
      prevOriginalContentStates.current[contentKey] = incomingHtml
    })
  }, [selectedLanguages])

  // Receive current collaborators list when joining a document room
  useEffect(() => {
    return websocketService.onWritingActiveUsersInDoc(event => {
      setDocumentCollaborators(prev => ({
        ...prev,
        [event.writing_id]: event.users.map(u => ({
          userId: u.user_id,
          userName: u.user_name,
        })),
      }))
    })
  }, [])

  // Track collaborators joining / leaving the document room
  useEffect(() => {
    const unsubJoined = websocketService.onWritingUserJoinedDoc(event => {
      setDocumentCollaborators(prev => {
        const existing = prev[event.writing_id] || []
        if (existing.some(c => c.userId === event.user_id)) return prev
        return {
          ...prev,
          [event.writing_id]: [
            ...existing,
            { userId: event.user_id, userName: event.user_name },
          ],
        }
      })
    })

    const unsubLeft = websocketService.onWritingUserLeftDoc(event => {
      setDocumentCollaborators(prev => ({
        ...prev,
        [event.writing_id]: (prev[event.writing_id] || []).filter(
          c => c.userId !== event.user_id
        ),
      }))
    })

    return () => {
      unsubJoined()
      unsubLeft()
    }
  }, [])

  // Clear collaborators for the document when we leave it
  useEffect(() => {
    return () => {
      if (activeWritingId) {
        setDocumentCollaborators(prev => {
          const rest = Object.fromEntries(
            Object.entries(prev).filter(([key]) => key !== activeWritingId)
          )
          return rest
        })
      }
    }
  }, [activeWritingId])

  // Get saving status text
  const getSavingStatusText = useCallback(
    (writingAssistantId: string) => {
      if (isSaving[writingAssistantId]) {
        return 'Opslaan...'
      }

      if (lastSavedAt[writingAssistantId]) {
        const secondsAgo = Math.floor(
          (Date.now() - lastSavedAt[writingAssistantId]!.getTime()) / 1000
        )
        if (secondsAgo < 60) {
          return `Automatisch opgeslagen ${secondsAgo} seconden geleden`
        } else {
          const minutesAgo = Math.floor(secondsAgo / 60)
          return `Automatisch opgeslagen ${minutesAgo} minuten geleden`
        }
      }

      return null
    },
    [isSaving, lastSavedAt]
  )

  // Get content state for a specific writing assistant and language
  const getContentState = useCallback(
    (writingAssistantId: string) => {
      const selectedLanguage = selectedLanguages[writingAssistantId] || 'nl'
      const contentKey = `${writingAssistantId}-${selectedLanguage}`
      return contentStates[contentKey] || ''
    },
    [contentStates, selectedLanguages]
  )

  // Get collaborators currently viewing a specific writing document
  const getCollaborators = useCallback(
    (writingId: string): CollaboratorInfo[] => documentCollaborators[writingId] || [],
    [documentCollaborators]
  )

  return {
    getContentState,
    isSaving,
    handleContentChange,
    saveContent,
    getSavingStatusText,
    getCurrentContent,
    getCollaborators,
  }
}
